import { useCallback } from 'react';

import { useIntl } from 'react-intl';

import { getMergeQueuePullRequestsUrl } from 'src/components/merge-queues/constants';
import { useFlag } from 'src/hooks/flag';
import { useMutation } from 'src/utils/fb-query';
import { authRequest, jsonHeaders } from 'src/utils/fetch';
import { captureExceptionWithTags } from 'src/utils/sentry';

import { messages } from '../merge-queues.i18n';
import { UsePullRequestActionParams } from '../types';

const movePullRequestToTopRequest = async ({
  mergeQueueUuid,
  pullRequestId,
  owner,
  slug,
}: Omit<UsePullRequestActionParams, 'onClose'>) => {
  const url = `${getMergeQueuePullRequestsUrl(
    owner,
    slug,
    mergeQueueUuid
  )}/${pullRequestId}/move-to-top`;

  const response = await fetch(
    authRequest(url, {
      method: 'POST',
      headers: jsonHeaders,
    })
  );

  if (!response.ok) {
    throw new Error(
      `Failed to move pull request to top: ${response.status} ${response.statusText}`
    );
  }

  return Promise.resolve();
};

export const useMovePullRequestToTop = ({
  mergeQueueUuid,
  owner,
  slug,
  onClose,
}: Omit<UsePullRequestActionParams, 'pullRequestId'>) => {
  const { showSuccessFlag, showErrorFlag } = useFlag();
  const { formatMessage } = useIntl();

  const movePullRequestToTopCallback = useCallback(
    (pullRequestId: number) =>
      movePullRequestToTopRequest({
        mergeQueueUuid,
        pullRequestId,
        owner,
        slug,
      }),
    [mergeQueueUuid, owner, slug]
  );

  const { mutate: movePullRequestToTop, isPending: isMovingPullRequestToTop } =
    useMutation(movePullRequestToTopCallback, {
      onSuccess: () => {
        showSuccessFlag(formatMessage(messages.moveToTopPullRequestSuccess));
      },
      onError: (error: Error) => {
        captureExceptionWithTags(error, {
          section: 'merge-queue-pull-request',
          action: 'move-pull-request-to-top',
        });
        showErrorFlag(formatMessage(messages.moveToTopPullRequestError));
      },
      onFinished: () => {
        onClose();
      },
    });

  return {
    movePullRequestToTop,
    isMovingPullRequestToTop,
  };
};
